import { styled } from "goober";
import { queries } from "../../shared/queries";
import { useQuery } from "../hooks/useQuery";
import { useRunExecutions } from "../hooks/useStepExecutions";
import type { Connector } from "../lib/connector";
import type { ParsedStep } from "../lib/parseCommitData";
import { WorkflowConnectorsPanel } from "./WorkflowConnectorsPanel";
import { WorkflowGraphPanel } from "./WorkflowGraphPanel";
import { WorkflowStepsPanel } from "./WorkflowStepsPanel";
import { WorkflowStepTestsPanel } from "./WorkflowStepTestsPanel";

type Props = {
  workflowId: string;
  branchId: string;
  steps: ReadonlyArray<ParsedStep>;
  connectors: ReadonlyArray<Connector>;
  onStepsHover?: (canonicalIds: ReadonlyArray<string> | null) => void;
  onAddTests?: () => void;
  onInvestigateFailingSteps?: (stepNames: readonly string[]) => void;
};

const BAR_HEIGHT = 160;

const Outer = styled("div")`
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  overflow-x: hidden;
  container-type: inline-size;
`;

const Card = styled("div")`
  display: flex;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;
`;

const GraphSlot = styled(Card)`
  flex: 1;
  min-height: 200px;
`;

const BarsRow = styled("div")`
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  gap: 12px;
  height: ${BAR_HEIGHT}px;

  @container (max-width: 600px) {
    flex-direction: column;
    height: auto;
  }
`;

const BarCard = styled(Card)`
  flex: 1;
  min-width: 0;
`;

export function WorkflowMonitorPanel({
  workflowId,
  branchId,
  steps,
  connectors,
  onStepsHover,
  onAddTests,
  onInvestigateFailingSteps,
}: Props) {
  const [runs, runsQuery] = useQuery(
    queries.loadBranchRunsSummary({ branchId })
  );
  const latestRunId = runs.find((r) => !r.ephemeral)?.id ?? null;
  const [latestRunRow] = useQuery(
    latestRunId ? queries.loadRunSummary({ runId: latestRunId }) : undefined
  );
  const { executions: latestRunExecutions } = useRunExecutions(latestRunId);
  const latestRun = latestRunRow
    ? { ...latestRunRow, executions: latestRunExecutions }
    : null;
  const showGhostGraph = runsQuery.type === "complete" && runs.length === 0;
  return (
    <Outer>
      <GraphSlot>
        <WorkflowGraphPanel
          runs={runs}
          latestRun={latestRun}
          ghost={showGhostGraph}
          ghostSeed={workflowId}
        />
      </GraphSlot>
      <BarsRow>
        <BarCard>
          <WorkflowStepsPanel
            steps={steps}
            onStepsHover={onStepsHover}
            onInvestigateFailingSteps={onInvestigateFailingSteps}
          />
        </BarCard>
        <BarCard>
          <WorkflowConnectorsPanel
            connectors={connectors}
            steps={steps}
            onStepsHover={onStepsHover}
          />
        </BarCard>
        <BarCard>
          <WorkflowStepTestsPanel
            workflowId={workflowId}
            branchId={branchId}
            onStepsHover={onStepsHover}
            onAddTests={onAddTests}
          />
        </BarCard>
      </BarsRow>
    </Outer>
  );
}
